// Core
import { useRef } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useAddNewCoefficientsMutation } from "state/coefficients/coefficientsApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

// List
import ListCoefficients from "./List";

const HREF_TEMPLATE = "/docs/template_coeff_fixed.csv";
const TASK_TYPE = "FIXED_COEFFICIENTS";

function ManageCoefficients() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["community"]);
  const fileInputRef = useRef(null);

  const activeTasks = useSelector(selectActiveTasksByType(TASK_TYPE));

  const [addCoefficients] = useAddNewCoefficientsMutation();
  const handleCoefficientsUpload = handleFileUpload(
    addCoefficients,
    community.id,
    dispatch,
    TASK_TYPE
  );

  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeTasks.length === 0,
    }
  );
  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: activeTasks,
    dispatch,
    TASK_TYPE,
  });

  const isUploading = activeTasks.length > 0;

  return (
    <div>
      <Crumbs
        items={[{ label: community?.name || "—" }, { label: t("fixedCoefficients.title") }]}
      />
      <PageHead
        title={t("fixedCoefficients.title")}
        subtitle={t("fixedCoefficients.subtitle")}
        actions={
          <div className="flex items-center gap-[10px] flex-wrap">
            {isUploading && (
              <Chip tone="amber">
                <Icon name="hourglass_top" style={{ fontSize: 13 }} />
                {t("fixedCoefficients.uploading")}
              </Chip>
            )}
            <a className="rec-btn-ghost rec-btn-sm no-underline" href={HREF_TEMPLATE}>
              <Icon name="download" style={{ fontSize: 16 }} />
              {t("fixedCoefficients.template")}
            </a>
            <UploadHelpButton size="md" />
            <Button
              variant="ghost"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="upload_file" />
              {t("fixedCoefficients.upload")}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCoefficientsUpload}
            />
          </div>
        }
      />
      <ListCoefficients />
    </div>
  );
}

export default ManageCoefficients;
